import * as React from 'react';
import {
    Button,
    CssBaseline,
    TextField,
    Link,
    Box,
    Typography,
    Container,
    IconButton,
    InputAdornment,
    createTheme,
    ThemeProvider,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './assets/insurly_logo.png';
import bgImg from './assets/bg.png';

function Copyright(props) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Copyright © '}
            <Link color="inherit" href="https://insurly.ch">
                Insurly
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#272B31',
        },
        secondary: {
            main: '#ff4081',
        },
        background: {
            default: '#f0f2f5',
        },
    },
    typography: {
        fontFamily: 'Inter, Roboto, Arial, sans-serif',
    },
});

export default function SignIn() {
    const [username, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem('insurlyUser'));
    useEffect(() => {
        if (user?.token) {
            navigate('/user-plans');
        }
    }, []);

    const loginUser = async (credentials) => {
        return fetch('/admin/api/v1/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
        }).then((res) => res);
    };

    const validateCredentials = (userName, password) => {
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userName);
        const passValid = password.length >= 6;

        if (!emailValid) {
            setError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
            return false;
        }

        setError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateCredentials(username, password)) return;

        const response = await loginUser({ email: username, password });
        if (response?.status === 200) {
            const res = await response.json();
            if (res?.payload) {
                localStorage.setItem(
                    'insurlyUser',
                    JSON.stringify({
                        email: username,
                        token: res.payload.token,
                        role: res.payload.role || null,
                        firstName: res.payload.firstName || '',
                        lastName: res.payload.lastName || '',
                        department: res.payload.department || ''
                    })
                );
                navigate('/user-plans');
            } else {
                setError('Unerwartete Antwort. Bitte versuchen Sie es erneut.');
            }
        } else {
            const res = await response.json().catch(() => ({}));
            switch (response.status) {
                case 400:
                    setError('Bitte füllen Sie beide Felder aus.');
                    break;
                case 401:
                    setError('Falscher Benutzername oder falsches Passwort.');
                    break;
                case 500:
                    setError(res?.error?.message || 'Serverfehler. Versuchen Sie es erneut.');
                    break;
                default:
                    setError('Etwas ist schiefgelaufen. Versuchen Sie es später erneut.');
            }
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    backgroundImage: `url(${bgImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 1
                    },
                }}
            >
                <Container
                    maxWidth="xs"
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        background: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <CssBaseline />
                    <Box sx={{ mb: 3 }}>
                        <img src={logo} alt="Insurly logo" style={{ width: '150px' }} />
                    </Box>
                    <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                        Einloggen
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{px: 2}}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Email"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={username}
                            onChange={(e) => setUserName(e.target.value)}
                            sx={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: '12px',
                                input: { borderRadius: '12px' },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Passwort"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                backgroundColor: '#f5f5f5',
                                borderRadius: '12px',
                                input: { borderRadius: '12px' },
                            }}
                        />
                        {error && (
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{ mt: 1, fontWeight: 500 }}
                            >
                                {error}
                            </Typography>
                        )}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2,
                                padding: '14px',
                                fontSize: '16px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #272B31, #1c1e22)',
                                textTransform: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1c1e22, #272B31)',
                                },
                            }}
                        >
                            Einloggen
                        </Button>
                    </Box>
                    <Copyright sx={{ mt: 4 }} />
                </Container>
            </Box>
        </ThemeProvider>
    );
}